import { Link } from 'react-router-dom';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Briefcase, CalendarOff, ClipboardCheck, FileWarning, UserCheck, UserMinus, UserPlus, Users, UserX,
} from 'lucide-react';
import { UserAvatar } from '../../../components/UserAvatar';
import type { OrgDashboard, PeopleDashboardData } from '../../../store/peopleDashboardStore';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP } from '../../../lib/chartColors';
import { cn } from '../../../lib/utils';
import {
  ApprovalQueuePanel, BarList, Empty, Kpi, Panel, SectionHeading, daysFrom, fmtDay, fmtTime, fmtWeekday, parseDay,
} from './widgets';

function AttendanceTrendPanel({ org, className }: { org: OrgDashboard; className?: string }) {
  const rows = org.attendanceTrend.map((r) => ({
    ...r,
    office: Math.max(0, r.present - r.remote),
    label: parseDay(r.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
  }));
  const days = rows.filter((r) => r.present > 0);
  const avg = days.length ? Math.round(days.reduce((s, r) => s + r.present, 0) / days.length) : 0;

  return (
    <Panel
      title="Attendance · last 14 days"
      description={days.length ? `Average ${avg} ${avg === 1 ? 'person' : 'people'} on days with check-ins` : 'No check-ins recorded in this period'}
      link={{ label: 'Attendance', to: '/people/attendance' }}
      className={className}
    >
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART_GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={CHART_TOOLTIP}
              labelFormatter={(_l: any, p: any) => (p?.[0]?.payload ? fmtWeekday(p[0].payload.date) : '')}
            />
            <Area type="monotone" dataKey="office" name="Office" stackId="a" stroke={CHART.blue} strokeWidth={2} fill={CHART.blue} fillOpacity={0.12} />
            <Area type="monotone" dataKey="remote" name="Remote" stackId="a" stroke={CHART.sky} strokeWidth={2} fill={CHART.sky} fillOpacity={0.12} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full" style={{ background: CHART.blue }} />Office</span>
        <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full" style={{ background: CHART.sky }} />Remote</span>
      </div>
    </Panel>
  );
}

function WorkforcePanel({ org, className }: { org: OrgDashboard; className?: string }) {
  return (
    <Panel title="Headcount by department" link={{ label: 'Employees', to: '/people/employees' }} className={className}>
      {org.byDepartment.length === 0 ? (
        <Empty icon={<Users />} text="No employees yet" />
      ) : (
        <>
          <BarList rows={org.byDepartment.slice(0, 6).map((d) => ({ label: d.department, value: d.count }))} />
          {org.byDepartment.length > 6 && (
            <p className="mt-2 text-xs text-muted-foreground">+{org.byDepartment.length - 6} more departments</p>
          )}
          {org.byEmploymentType.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5 border-t border-border pt-3">
              {org.byEmploymentType.map((t) => (
                <span key={t.type} className="rounded-md bg-secondary px-2 py-0.5 text-xs capitalize text-muted-foreground">
                  {t.type.replace(/[-_]/g, ' ')} · <span className="font-medium text-foreground">{t.count}</span>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function HiringPanel({ org, className }: { org: OrgDashboard; className?: string }) {
  const r = org.recruitment;
  const candidates = r.candidatesByStatus.reduce((s, c) => s + c.count, 0);
  return (
    <Panel
      title="Hiring"
      description={`${r.openings} open ${r.openings === 1 ? 'position' : 'positions'} across ${r.openJobs} ${r.openJobs === 1 ? 'job' : 'jobs'} · ${candidates} candidates`}
      link={{ label: 'Pipeline', to: '/people/recruitment' }}
      className={className}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Candidates by stage</p>
          {r.candidatesByStatus.length === 0 ? (
            <Empty text="No candidates yet" className="min-h-0 py-3" />
          ) : (
            <BarList rows={r.candidatesByStatus.map((c) => ({ label: c.status.replace(/[-_]/g, ' ').replace(/^\w/, (x) => x.toUpperCase()), value: c.count }))} />
          )}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Upcoming interviews</p>
          {r.upcomingInterviews.length === 0 ? (
            <Empty text="Nothing scheduled" className="min-h-0 py-3" />
          ) : (
            <ul className="space-y-2.5">
              {r.upcomingInterviews.map((i) => (
                <li key={i.id} className="min-w-0">
                  <p className="truncate text-sm text-foreground">{i.candidate}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {new Date(i.at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · {fmtTime(i.at)}
                    {i.type ? ` · ${i.type}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}

function PeopleListPanel({ title, link, empty, rows, className }: {
  title: string;
  link: { label: string; to: string };
  empty: string;
  rows: { id: string; name: string; sub: string; right: string }[];
  className?: string;
}) {
  return (
    <Panel title={title} link={link} className={className}>
      {rows.length === 0 ? (
        <Empty text={empty} />
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id}>
              <Link to={`/people/employees/${r.id}`} className="flex items-center gap-3 py-2 hover:text-primary">
                <UserAvatar name={r.name} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">{r.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{r.sub}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{r.right}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function LifecyclePanel({ org, className }: { org: OrgDashboard; className?: string }) {
  const items = [
    { label: 'Onboarding in progress', value: org.lifecycle.onboarding, to: '/people/onboarding', icon: <UserPlus />, warn: false },
    { label: 'Offboarding in progress', value: org.lifecycle.offboarding, to: '/people/offboarding', icon: <UserMinus />, warn: false },
    { label: 'Documents expiring in 30 days', value: org.lifecycle.documentsExpiring, to: '/people/documents', icon: <FileWarning />, warn: true },
  ];
  return (
    <Panel title="Employee lifecycle" className={className}>
      <ul className="space-y-1">
        {items.map((i) => (
          <li key={i.label}>
            <Link to={i.to} className="flex items-center gap-3 rounded-md px-2 py-2 -mx-2 hover:bg-secondary">
              <span className={cn(
                'flex size-8 items-center justify-center rounded-md [&_svg]:size-4',
                i.warn && i.value > 0 ? 'bg-warning/10 text-warning-strong' : 'bg-secondary text-muted-foreground'
              )}>
                {i.icon}
              </span>
              <span className="flex-1 text-sm text-foreground">{i.label}</span>
              <span className="font-display text-card-title tabular-nums text-foreground">{i.value}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** HR Admin / Super Admin: company-wide workforce, approvals and hiring. */
export function OrgSection({ data }: { data: PeopleDashboardData }) {
  const org = data.org!;
  const t = org.attendanceToday;
  const approvals = org.pendingLeave.total + org.pendingRegularizations.total;

  return (
    <section className="space-y-4">
      <SectionHeading title="Organization" description="Company-wide workforce, attendance and hiring" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Headcount" value={org.headcount.total} icon={<Users />} tone="primary" to="/people/employees"
          hint={org.headcount.joined30d ? `+${org.headcount.joined30d} joined in 30 days` : `${org.headcount.active} active`} />
        <Kpi label="Present today" value={t.present} icon={<UserCheck />} tone="success" to="/people/attendance"
          hint={`${t.rate}% · ${t.ongoing} working now`} />
        <Kpi label="On leave today" value={t.onLeave} icon={<CalendarOff />} tone="warning" to="/people/leave"
          hint="Approved leave" />
        <Kpi label="Not checked in" value={t.notCheckedIn} icon={<UserX />} to="/people/attendance"
          hint="Yet to check in today" />
        <Kpi label="Approvals" value={approvals} icon={<ClipboardCheck />} tone={approvals ? 'warning' : 'default'}
          to="/people/leave" hint={`${org.pendingLeave.total} leave · ${org.pendingRegularizations.total} attendance`} />
        <Kpi label="Open positions" value={org.recruitment.openings} icon={<Briefcase />} tone="info" to="/people/recruitment"
          hint={`${org.recruitment.openJobs} open ${org.recruitment.openJobs === 1 ? 'job' : 'jobs'}`} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <AttendanceTrendPanel org={org} className="lg:col-span-8" />
        <WorkforcePanel org={org} className="lg:col-span-4" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <ApprovalQueuePanel
          leave={org.pendingLeave}
          regularizations={org.pendingRegularizations}
          today={data.today}
          scopeLabel="Across the company, oldest first"
          className="lg:col-span-5"
        />
        <HiringPanel org={org} className="lg:col-span-7" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <PeopleListPanel
          title="New joiners"
          link={{ label: 'Directory', to: '/people/employees' }}
          empty="No recent joiners"
          rows={org.recentJoiners.map((j) => ({
            id: j.id,
            name: j.name,
            sub: [j.designation, j.department].filter(Boolean).join(' · ') || 'Role not set',
            right: fmtDay(j.joinDate),
          }))}
        />
        <PeopleListPanel
          title="Work anniversaries"
          link={{ label: 'Directory', to: '/people/employees' }}
          empty="No anniversaries in the next 30 days"
          rows={org.anniversaries.map((a) => ({
            id: a.id,
            name: a.name,
            sub: `${a.years} ${a.years === 1 ? 'year' : 'years'} · ${fmtDay(a.date)}`,
            right: daysFrom(data.today, a.date),
          }))}
        />
        <LifecyclePanel org={org} className="md:col-span-2 lg:col-span-1" />
      </div>
    </section>
  );
}

